import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Copy, CheckCheck, Plus } from 'lucide-react';

/**
 * CopyableBlock - A component that wraps content and provides copy functionality
 * Supports multiple copy formats with a dropdown menu
 */
const CopyableBlock = ({ children, className = '', getCopyContent, copyOptions = [], onInsert }) => {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
      setCopiedFormat(null);
    }, 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') return;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const resolveContent = (option) => {
    if (option?.getContent) {
      return option.getContent(contentRef.current);
    }
    if (getCopyContent) {
      return getCopyContent(contentRef.current);
    }
    const fallbackText = contentRef.current?.innerText || contentRef.current?.textContent || '';
    return { text: fallbackText };
  };

  const handleCopyBlock = async (option = null) => {
    const resolved = resolveContent(option);
    const text = resolved?.text;
    const html = resolved?.html;

    if (!(text && text.trim()) && !(html && html.trim())) {
      toast.warning('No hay contenido para copiar');
      return;
    }

    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        throw new Error('Clipboard API no disponible');
      }

      if (html && typeof ClipboardItem !== 'undefined') {
        const plainText = (text || html).replace(/\u00A0/g, ' ').trimEnd();
        const blobPlain = new Blob([plainText], { type: 'text/plain' });
        const blobHtml = new Blob([html], { type: 'text/html' });
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': blobPlain,
            'text/html': blobHtml,
          }),
        ]);
      } else {
        await navigator.clipboard.writeText((text || '').replace(/\u00A0/g, ' ').trimEnd());
      }

      setCopied(true);
      if (copyOptions.length) {
        setMenuOpen(false);
        setCopiedFormat(option?.label || null);
      }
    } catch (error) {
      console.error('Error copying block:', error);
      toast.error('No se pudo copiar este bloque');
    }
  };

  const handleCopyButtonClick = () => {
    if (copyOptions.length) {
      setMenuOpen((prev) => !prev);
    } else {
      handleCopyBlock();
    }
  };

  return (
    <div ref={containerRef} className={`copyable-block relative group pt-9 ${className}`}>
      {/* T-143: los botones viven en la franja reservada por `pt-9` ARRIBA del
          bloque, no encima del contenido — antes tapaban la última celda del
          encabezado de las tablas al hacer hover. Siguen apareciendo en hover. */}
      <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 transition-all ${
        copied ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
      }`}>
        {onInsert && (
          <button
            type="button"
            onClick={() => onInsert(contentRef.current)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-green-200 dark:border-green-800 bg-white/90 dark:bg-slate-800/90 text-green-600 dark:text-green-400 shadow-sm transition-all hover:bg-green-50 dark:hover:bg-green-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-300"
            aria-label="Insertar tabla en documento"
            title="Insertar tabla en documento"
            data-testid="insert-block-button"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
        <button
          type="button"
          onClick={handleCopyButtonClick}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
          aria-label={copyOptions.length ? 'Elegir formato de copia' : 'Copiar bloque'}
          data-testid="copy-block-button"
        >
          {copied ? <CheckCheck className="w-3 h-3 text-green-600 dark:text-green-400" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>

      {copyOptions.length > 0 && menuOpen && (
        <div className="absolute top-12 right-0 z-20 w-44 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-2 shadow-xl ring-1 ring-black/5">
          <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Copiar como</p>
          <div className="space-y-1">
            {copyOptions.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleCopyBlock(option)}
                className={`flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors ${
                  copiedFormat === option.label
                    ? 'bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border border-sky-100 dark:border-sky-800'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <span>{option.label}</span>
                {copiedFormat === option.label && <CheckCheck className="h-3 w-3 text-sky-600 dark:text-sky-400" />}
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default CopyableBlock;
